import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

// Cache for storing fetched candidates data per job
const candidatesCache = new Map();
const lastFetchTimes = new Map();
const CACHE_DURATION = 30 * 1000; // 30 seconds — short enough to show fresh scores after retakes

const useCandidate = (jobId) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isInitialMount = useRef(true);

  const fetchCandidates = useCallback(async (forceRefresh = false) => {
    // Check if we have valid cached data
    const now = Date.now();
    const lastFetchTime = lastFetchTimes.get(jobId) || 0;
    
    if (!forceRefresh && candidatesCache.has(jobId) && (now - lastFetchTime) < CACHE_DURATION) {
      setCandidates(candidatesCache.get(jobId));
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';
      
      // Using the AI evaluator backend directly with credentials
      const response = await axios.get(getApiUrl(`/api/candidatelist?jobId=${jobId}`), {
        withCredentials: true,
        headers: {
          'X-Admin-Email': adminEmail
        }
      });
      
      // Cache the fetched data
      candidatesCache.set(jobId, response.data);
      lastFetchTimes.set(jobId, now);
      
      setCandidates(response.data);
      // Debug log removed — was leaking full candidate list (PII: names,
      // emails, resume scores) into the browser console on every refetch.
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch candidates.');
      console.error('Error fetching candidates:', err);
      
      // If there's an error but we have cached data, use that instead
      if (candidatesCache.has(jobId)) {
        setCandidates(candidatesCache.get(jobId));
      }
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      // Only fetch on initial mount or if cache is expired
      if (isInitialMount.current || !candidatesCache.has(jobId) || (Date.now() - (lastFetchTimes.get(jobId) || 0)) >= CACHE_DURATION) {
        fetchCandidates();
        isInitialMount.current = false;
      } else {
        // Use cached data immediately
        setCandidates(candidatesCache.get(jobId));
        setLoading(false);
        setError(null);
      }
    }
  }, [jobId, fetchCandidates]);

  // Refetch when user returns to this tab/window (e.g. after viewing reports page)
  // This ensures scores are always fresh after a candidate retakes a test
  useEffect(() => {
    const handleFocus = () => {
      if (jobId) {
        candidatesCache.delete(jobId);
        lastFetchTimes.delete(jobId);
        fetchCandidates(true);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [jobId, fetchCandidates]);

  // Function to refresh candidates (force fresh fetch)
  const refreshCandidates = useCallback(async () => {
    // Clear cache first to ensure fresh data
    if (jobId) {
      candidatesCache.delete(jobId);
      lastFetchTimes.delete(jobId);
    }
    await fetchCandidates(true);
  }, [fetchCandidates, jobId]);

  // Function to clear cache for a specific job
  const clearCache = useCallback(() => {
    if (jobId) {
      candidatesCache.delete(jobId);
      lastFetchTimes.delete(jobId);
    }
  }, [jobId]);

  // Function to clear all cache
  const clearAllCache = useCallback(() => {
    candidatesCache.clear();
    lastFetchTimes.clear();
  }, []);

  return { 
    candidates, 
    loading, 
    error, 
    refetch: fetchCandidates,
    refreshCandidates,
    clearCache,
    clearAllCache
  };
};

export default useCandidate; 